import type { AttendanceHistoryEntry } from '../types';

/**
 * Attendance edit-audit logic. PURE and firebase-free on purpose: attendance writes
 * live in dataService (which initializes Firebase at import), so keeping the diff
 * here lets it be unit-tested without Firebase credentials — the same reason
 * studentPurge.ts exists. dataService imports this and does the I/O.
 */

/** Strip top-level undefined values (Firestore rejects undefined). Keeps null —
 *  `previousMark: null` / `newMark: null` are MEANINGFUL (first mark / removed mark). */
function stripUndefined<T extends Record<string, unknown>>(o: T): T {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(o)) if (o[k] !== undefined) out[k] = o[k];
  return out as T;
}

/**
 * Diff a day's attendance before/after a save into audit entries — one per student
 * whose mark, late time, or note actually CHANGED. An unchanged re-save produces
 * nothing, so the trail records edits, not saves. `actor` is stamped on every entry
 * so an inspection can answer "who changed this, and from what?".
 */
export function diffAttendanceDay(
  date: string,
  before: { records?: Record<string, string>; lateTimes?: Record<string, string>; notes?: Record<string, string> } | undefined,
  after: { records?: Record<string, string>; lateTimes?: Record<string, string>; notes?: Record<string, string> },
  actor: { uid?: string; name?: string; at: string },
): AttendanceHistoryEntry[] {
  const b = { records: before?.records || {}, lateTimes: before?.lateTimes || {}, notes: before?.notes || {} };
  const a = { records: after.records || {}, lateTimes: after.lateTimes || {}, notes: after.notes || {} };
  const studentIds = new Set([
    ...Object.keys(b.records), ...Object.keys(a.records),
    ...Object.keys(b.lateTimes), ...Object.keys(a.lateTimes),
    ...Object.keys(b.notes), ...Object.keys(a.notes),
  ]);
  const out: AttendanceHistoryEntry[] = [];
  for (const sid of studentIds) {
    // ?? null normalizes undefined → null, so a legacy day with no mark re-saved with
    // no mark compares equal (no phantom entry).
    const pm = (b.records[sid] ?? null) as AttendanceHistoryEntry['previousMark'];
    const nm = (a.records[sid] ?? null) as AttendanceHistoryEntry['newMark'];
    const pl = b.lateTimes[sid];
    const nl = a.lateTimes[sid];
    const pn = b.notes[sid];
    const nn = a.notes[sid];
    if (pm === nm && pl === nl && pn === nn) continue; // unchanged → no entry
    out.push(stripUndefined({
      id: `${date}_${sid}_${actor.at}`,
      studentId: sid,
      date,
      previousMark: pm,
      newMark: nm,
      ...(pl !== nl ? { previousLateTime: pl, newLateTime: nl } : {}),
      ...(pn !== nn ? { previousNote: pn, newNote: nn } : {}),
      changedAt: actor.at,
      ...(actor.uid ? { changedByUid: actor.uid } : {}),
      ...(actor.name ? { changedByName: actor.name } : {}),
    }) as AttendanceHistoryEntry);
  }
  return out;
}
